import { Button } from "@/components/ui/button";
import { ConfirmAlertDialog } from "@/components/ui/confirm-alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { useState } from "react";
import { resolveNotificationSlugs } from "../api/notifications-api";
import {
  useDeleteAllReadNotificationsMutation,
  useDeleteNotificationMutation,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useMarkNotificationUnreadMutation,
  useNotificationsQuery,
  useNotificationsSubscription,
} from "../api/notifications-hooks";
import { SCROLL_TO_MENTION_EVENT, docMentionTarget } from "../doc-mention-target";
import { domainInviteFullTarget } from "../domain-invite-full-target";
import { isJoinRequestNotification, joinRequestTarget } from "../join-request-target";
import type { Notification } from "../notifications.types";
import { isShareNotification, shareTarget } from "../share-target";
import { NotificationItem } from "./notification-item";

export function NotificationBell({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: notifications } = useNotificationsQuery(userId);
  useNotificationsSubscription(userId);

  const { mutate: markRead } = useMarkNotificationReadMutation(userId);
  const { mutate: markUnread } = useMarkNotificationUnreadMutation(userId);
  const { mutate: markAllRead } = useMarkAllNotificationsReadMutation(userId);
  const { mutate: deleteNotification } = useDeleteNotificationMutation(userId);
  const { mutate: deleteAllRead } = useDeleteAllReadNotificationsMutation(userId);

  const unread = (notifications ?? []).filter((n) => !n.read_at);
  const hasUnread = unread.length > 0;
  const hasRead = (notifications ?? []).some((n) => !!n.read_at);
  const displayed = showAll ? (notifications ?? []) : unread;

  function handleNotificationClick(notification: Notification) {
    if (!notification.read_at) {
      markRead(notification.id);
    }

    if (notification.type === "workspace_invite" || notification.type === "project_invite") {
      if (notification.invite_code) {
        void navigate({
          to: "/invite/$code",
          params: { code: notification.invite_code },
        });
      }
      return;
    }

    // CR-185: may have no project slug, so it branches before the both-slugs guard.
    if (notification.type === "domain_invite_full") {
      const target = domainInviteFullTarget(notification);
      if (target) {
        void navigate(target);
      }
      return;
    }

    const slugs = resolveNotificationSlugs(notification);
    if (!slugs) return;

    if (isJoinRequestNotification(notification)) {
      void navigate(joinRequestTarget(notification.type, slugs));
      return;
    }

    if (notification.type === "project_member_added") {
      void navigate({
        to: "/workspaces/$slug/projects/$projectSlug",
        params: {
          slug: slugs.workspaceSlug,
          projectSlug: slugs.projectSlug,
        },
      });
      return;
    }

    if (!notification.container_id) return;
    // CR-263: opens the shared item; nothing inside it targets the recipient,
    // so no SCROLL_TO_MENTION_EVENT and no message anchor.
    if (isShareNotification(notification)) {
      void navigate(shareTarget(notification.type, notification.container_id, slugs));
      return;
    }
    if (notification.type === "doc_mention" || notification.type === "comment_mention") {
      const { search, eventDetail } = docMentionTarget(notification.container_id, notification);
      // Dispatch event so SimpleEditor can target immediately if already on this page
      window.dispatchEvent(new CustomEvent(SCROLL_TO_MENTION_EVENT, { detail: eventDetail }));
      void navigate({
        to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
        params: {
          slug: slugs.workspaceSlug,
          projectSlug: slugs.projectSlug,
          docId: notification.container_id,
        },
        search,
      });
    } else {
      void navigate({
        to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
        params: {
          slug: slugs.workspaceSlug,
          projectSlug: slugs.projectSlug,
          chatId: notification.container_id,
        },
        search: {
          messageId: notification.source_id ?? undefined,
          draft: undefined,
        },
      });
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <BellIcon className="size-5" />
            {hasUnread ? (
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500" />
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-3 py-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowAll(false)}
                className={cn(
                  "rounded px-2 py-0.5",
                  !showAll
                    ? "bg-background font-medium shadow-sm hover:bg-background"
                    : "text-muted-foreground",
                )}
              >
                Unread
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowAll(true)}
                className={cn(
                  "rounded px-2 py-0.5",
                  showAll
                    ? "bg-background font-medium shadow-sm hover:bg-background"
                    : "text-muted-foreground",
                )}
              >
                All
              </Button>
            </div>
          </div>
          <DropdownMenuSeparator className="my-0" />
          {(hasUnread && !showAll) || hasRead ? (
            <div className="flex justify-end gap-1 px-3 py-0.5">
              {hasUnread && !showAll ? (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => markAllRead()}
                  className="h-auto px-2 py-1"
                >
                  Mark all as read
                </Button>
              ) : null}
              {hasRead && showAll ? (
                <Button
                  variant="outline-destructive"
                  size="xs"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="h-auto border-none px-2 py-1"
                >
                  Delete all read notifications
                </Button>
              ) : null}
            </div>
          ) : null}
          <div className="max-h-80 overflow-y-auto">
            {displayed.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                {showAll ? "No notifications" : "No unread notifications"}
              </div>
            ) : (
              displayed.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                  onMarkRead={markRead}
                  onMarkUnread={markUnread}
                  onDelete={deleteNotification}
                />
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete all read notifications"
        description="This will permanently delete all your read notifications. This cannot be undone."
        confirmLabel="Delete all"
        onConfirm={() => deleteAllRead()}
      />
    </>
  );
}
